import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Headline2 } from 'pages/UserProfile/Header/styled';
import { StripeWordmark } from 'pages/Subscriptions/SubscriptionInner/PaymentBrandIcons';
import { SubscriptionFrequency, UserSubscription } from 'types';
import { formatMoney } from 'utils/money';

import { TRIAL_DEFAULTS } from './constants';
import InvoiceHistory from './InvoiceHistory';
import PaymentDetailsCard from './PaymentDetailsCard';
import PlanFeatureList from './PlanFeatureList';
import SubscriptionStatusBanner from './SubscriptionStatusBanner';
import UsageMetersCard from './UsageMetersCard';
import type { SubscriptionProfileModel } from './useSubscriptionProfileModel';
import {
  IconCalendar,
  IconChevronDown,
  IconChevronRight,
  IconLock,
} from './icons';
import {
  ActionRow,
  Badge,
  Card,
  CardHeaderRow,
  CardSubtitle,
  ChoosePlanFullButton,
  DropdownItem,
  DropdownPanel,
  FooterNote,
  FooterStripeNote,
  PlanFooter,
  PlanName,
  SplitButtonChevron,
  SplitButtonMain,
  SplitButtonWrap,
  TextActionLink,
  TrustRow,
  TwoColGrid,
} from './styled';

type SubscribedPlanViewProps = {
  model: SubscriptionProfileModel;
  changePlanPath: string;
  cancelSubscriptionPath: string;
};

const SubscribedPlanView = ({
  model,
  changePlanPath,
  cancelSubscriptionPath,
}: SubscribedPlanViewProps) => {
  const { t } = useTranslation();
  const [planMenuOpen, setPlanMenuOpen] = useState(false);
  const planMenuRef = useRef<HTMLDivElement>(null);

  const subscription = model.subscription as UserSubscription;

  useEffect(() => {
    if (!planMenuOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (
        planMenuRef.current &&
        !planMenuRef.current.contains(e.target as Node)
      ) {
        setPlanMenuOpen(false);
      }
    };
    document.addEventListener('click', onDoc);
    return () => document.removeEventListener('click', onDoc);
  }, [planMenuOpen]);

  const {
    isPaidActive,
    isTrial,
    isFullyCancelled,
    isLegacyCancelled,
    isPlanTrial,
    isStripeNonRenewingActive,
    isInvoiceBillingLayout,
    billingAttentionReason,
    planName,
    subscriptionEndDate,
    nextPaymentLabel,
    paymentMethodLine,
    billingPortalUrl,
  } = model;

  const paidProExtras =
    isPaidActive && !isTrial
      ? (
          t('subscriptions.profile.plan.paidProFeatureExtras', {
            returnObjects: true,
          }) as string[]
        ).filter(Boolean)
      : [];

  const planSubtitle = isTrial
    ? t('subscriptions.profile.plan.subtitleTrial')
    : isLegacyCancelled
    ? `${t('subscriptions.profile.labels.validUntil')} ${subscriptionEndDate}`
    : isFullyCancelled
    ? t('subscriptions.profile.plan.subtitleFullyCancelled', {
        plan: planName,
        date: subscriptionEndDate,
      })
    : isPaidActive && isStripeNonRenewingActive
    ? t('subscriptions.profile.plan.subtitleNonRenewal', {
        date: subscriptionEndDate,
      })
    : t('subscriptions.profile.plan.subtitleActive');

  const badge = isTrial ? (
    <Badge $tone="teal">{t('subscriptions.profile.plan.badgeTrial')}</Badge>
  ) : isLegacyCancelled ? (
    <Badge $tone="slate">
      {t('subscriptions.profile.plan.badgeCancelled')}
    </Badge>
  ) : isFullyCancelled ? (
    <Badge $tone="slate">
      {planName} · {t('subscriptions.profile.plan.badgeCancelled')}
    </Badge>
  ) : isPaidActive ? (
    <Badge $tone="teal">
      {planName} · {t('subscriptions.profile.plan.badgeActive')}
    </Badge>
  ) : (
    <Badge $tone="teal">{t('subscriptions.profile.plan.badgeActive')}</Badge>
  );

  const billingMethod = isInvoiceBillingLayout
    ? t('subscriptions.profile.plan.paymentMethodBilledByInvoice')
    : t('subscriptions.profile.plan.paymentMethodChargedTo', {
        chargedTo:
          paymentMethodLine ||
          t('subscriptions.profile.plan.paymentMethodChargedGeneric'),
      });

  const footerNote = isTrial ? (
    <FooterNote>
      <IconCalendar />
      {t('subscriptions.profile.plan.endsOnNote', {
        date: subscriptionEndDate,
      })}
    </FooterNote>
  ) : (isPaidActive || isPlanTrial) && subscription.subscriptionPlan ? (
    <FooterNote>
      <IconCalendar />
      {isFullyCancelled
        ? t('subscriptions.profile.plan.footnoteFullyCancelled', {
            date: subscriptionEndDate,
          })
        : isStripeNonRenewingActive
        ? t('subscriptions.profile.plan.footnoteRenewalCancelled', {
            date: subscriptionEndDate,
            price: formatMoney(
              subscription.subscriptionPlan.currency,
              subscription.frequency === SubscriptionFrequency.YEAR
                ? subscription.subscriptionPlan.yearlyValue
                : subscription.subscriptionPlan.monthlyValue,
            ),
            period:
              subscription.frequency === SubscriptionFrequency.YEAR
                ? t('changePlan.perYear')
                : t('changePlan.perMonth'),
            billingMethod,
          })
        : isPlanTrial
        ? t('subscriptions.profile.plan.footnoteTrialCharge', {
            trialEndDate: subscriptionEndDate,
            planPrice: `${formatMoney(
              subscription.subscriptionPlan.currency,
              subscription.proratedValue,
            )}${
              subscription.frequency === SubscriptionFrequency.YEAR
                ? t('changePlan.perYear')
                : t('changePlan.perMonth')
            }`,
            billingMethod,
          })
        : t('subscriptions.profile.plan.footnoteRenewalPayment', {
            date: subscriptionEndDate,
            price: formatMoney(
              subscription.subscriptionPlan.currency,
              subscription.proratedValue,
            ),
            period:
              subscription.frequency === SubscriptionFrequency.YEAR
                ? t('changePlan.perYear')
                : t('changePlan.perMonth'),
            billingMethod,
          })}
    </FooterNote>
  ) : (
    <FooterNote>
      <IconCalendar />
      {nextPaymentLabel} {subscriptionEndDate}
    </FooterNote>
  );

  const planActions = (() => {
    // Cancelled (legacy or fully ended): always offer Choose plan so they can
    // resubscribe — including when an overdue/unpaid banner is also showing.
    if (isFullyCancelled || isLegacyCancelled) {
      return (
        <ActionRow>
          <ChoosePlanFullButton as={Link} to={changePlanPath}>
            {t('subscriptions.profile.actions.choosePlan')}
            <IconChevronRight />
          </ChoosePlanFullButton>
        </ActionRow>
      );
    }

    // Active subscription with outstanding invoice / unpaid / past_due:
    // banner owns the pay/update CTA — don't offer Choose plan yet.
    if (billingAttentionReason) {
      return null;
    }

    if (isTrial) {
      return (
        <ActionRow>
          <ChoosePlanFullButton as={Link} to={changePlanPath}>
            {t('subscriptions.profile.actions.choosePlan')}
            <IconChevronRight />
          </ChoosePlanFullButton>
        </ActionRow>
      );
    }

    return (
      <ActionRow>
        <SplitButtonWrap ref={planMenuRef}>
          <SplitButtonMain as={Link} to={changePlanPath}>
            {t('subscriptions.profile.actions.changePlan')}
          </SplitButtonMain>
          <SplitButtonChevron
            type="button"
            aria-expanded={planMenuOpen}
            aria-label="Plan options"
            onClick={(e) => {
              e.stopPropagation();
              setPlanMenuOpen((o) => !o);
            }}
          >
            <IconChevronDown />
          </SplitButtonChevron>
          <DropdownPanel $open={planMenuOpen}>
            <DropdownItem
              as={Link}
              to={changePlanPath}
              onClick={() => setPlanMenuOpen(false)}
            >
              {t('subscriptions.profile.actions.choosePlan')}
            </DropdownItem>
            <DropdownItem
              as={Link}
              to={changePlanPath}
              onClick={() => setPlanMenuOpen(false)}
            >
              {t('subscriptions.profile.actions.comparePlansMenu')}
            </DropdownItem>
          </DropdownPanel>
        </SplitButtonWrap>
        {isPaidActive && !isTrial && !isFullyCancelled ? (
          isStripeNonRenewingActive ? (
            billingPortalUrl ? (
              <TextActionLink
                href={billingPortalUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('subscriptions.profile.actions.dontCancelSubscription')}
              </TextActionLink>
            ) : null
          ) : (
            <TextActionLink as={Link} to={cancelSubscriptionPath}>
              {t('subscriptions.profile.actions.cancelSubscription')}
            </TextActionLink>
          )
        ) : null}
      </ActionRow>
    );
  })();

  const paymentTrustFooter = (
    <FooterStripeNote>
      <IconLock size={16} />
      <span>{t('subscriptions.profile.trust.poweredBy')}</span>
      <StripeWordmark height={16} />
    </FooterStripeNote>
  );

  return (
    <>
      <SubscriptionStatusBanner
        subscription={subscription}
        planName={planName}
        subscriptionEndDate={subscriptionEndDate}
        isPlanTrial={isPlanTrial}
        isTrial={isTrial}
        isFullyCancelled={isFullyCancelled}
        isLegacyCancelled={isLegacyCancelled}
        isPaidActive={isPaidActive}
        isStripeNonRenewingActive={isStripeNonRenewingActive}
        billingAttentionReason={billingAttentionReason}
        billingAttentionActionUrl={model.billingAttentionActionUrl}
      />
      <TwoColGrid>
        <Card>
          <CardHeaderRow>
            <div>
              <Headline2>{t('subscriptions.profile.plan.title')}</Headline2>
              <CardSubtitle>{planSubtitle}</CardSubtitle>
            </div>
            {badge}
          </CardHeaderRow>
          <PlanName $muted={isLegacyCancelled || isFullyCancelled}>
            {planName}
          </PlanName>
          <PlanFeatureList
            maxCommunities={
              model.maxCommunities || TRIAL_DEFAULTS.maxCommunities
            }
            maxGroups={model.maxGroups || TRIAL_DEFAULTS.maxGroups}
            maxMembers={model.maxUsers || TRIAL_DEFAULTS.maxMembers}
            maxStorage={model.maxBucketSize || TRIAL_DEFAULTS.maxStorage}
            extras={paidProExtras}
            style={{ marginBottom: '28px' }}
          />
          <PlanFooter>
            {footerNote}
            {planActions}
          </PlanFooter>
        </Card>
        <PaymentDetailsCard
          subscription={model.subscription}
          isInvoiceBillingLayout={model.isInvoiceBillingLayout}
          showBillingPaymentDetails={model.showBillingPaymentDetails}
          showSwitchBillingMode={model.showSwitchBillingMode}
          isChfCurrency={model.isChfCurrency}
          canUseManageBilling={model.canUseManageBilling}
          billingPortalUrl={model.billingPortalUrl}
          billingCyclePrimaryText={model.billingCyclePrimaryText}
          translatedPaymentCycle={model.translatedPaymentCycle}
          paymentMethodLine={model.paymentMethodLine}
          cardExpiry={model.cardExpiry}
          lastInvoiceMeta={model.lastInvoiceMeta}
          lastInvoicePdfUrl={model.lastInvoicePdfUrl}
          invoiceContactEmail={model.invoiceContactEmail}
          onViewInvoices={model.scrollToInvoiceHistory}
        />
      </TwoColGrid>
      <UsageMetersCard
        locked={false}
        showUsageGuidance={!isFullyCancelled}
        lockedHintKey={
          isFullyCancelled
            ? 'subscriptions.profile.usage.lockedHintResubscribe'
            : 'subscriptions.profile.usage.lockedHint'
        }
        dimmed={isFullyCancelled}
        changePlanPath={changePlanPath}
        isSubscriptionOwner={model.isSubscriptionOwner}
        maxMembers={model.maxUsers}
        maxCommunities={model.maxCommunities}
        maxGroups={model.maxGroups}
        maxStorage={model.maxBucketSize}
        usedMembers={model.usedMembers}
        usedCommunities={model.usedCommunities}
        usedGroups={model.usedGroups}
        usedStorage={model.usedStorage}
      />
      {model.isChfCurrency ? <InvoiceHistory /> : null}
      <TrustRow>{paymentTrustFooter}</TrustRow>
    </>
  );
};

export default SubscribedPlanView;
